/**
 * @desc        IndexedDB hooks. The DB instance and schema now live in
 *              src/services/db/farmdeckDb.js and are shared between React
 *              consumers (via DBProvider/DBContext) and plain service modules
 *              (via getDb()). This file owns only the React integration and
 *              the feature-level hooks (useUserSettings, useMobAnimals).
 * @version     2.0.1 (useUserSettings panels support batch params.updates for exclusive toggles) - 23 July 2026
 * */

import React, { useState, useEffect, createContext, useContext, useRef, useMemo, useCallback } from 'react'
import fastCompare from 'react-fast-compare'
import { getSessionStorage, setSessionStorage } from 'helper-functions/objects/storage.js'
import { DEFAULT_PAGE_SETTINGS } from './default-dashboard-panel-schema.js'
import { getDb } from 'services/db/farmdeckDb.js'

const TIMEOUT_DURATION = 10000

const DBContext = createContext()

export const DBProvider = ({ children }) => {
    const [db, setDb] = useState(null)
    useEffect(() => {
        let cancelled = false
        getDb()
            .then(dbInstance => {
                if (cancelled) return
                /*
                 * Back-compat: existing code (useSettings polling, test harnesses)
                 * reads window.__dexieDbInstance. Keep it wired to the singleton
                 * so nothing breaks while we migrate callers to getDb().
                 */
                window.__dexieDbInstance = dbInstance
                setDb(dbInstance)
            })
            .catch(err => {
                console.error('FarmDeck DB failed to open', err)
            })
        return () => {
            cancelled = true
            /*
             * Intentionally do not close the DB here. The singleton owns the
             * lifecycle and <DBProvider> lives at the app root; the previous
             * close-on-unmount was the cause of the DatabaseClosedError retries
             * downstream.
             */
        }
    }, [])
    // Render children only after db is initialized
    return db ? <DBContext.Provider value={db}>{children}</DBContext.Provider> : null
}

const useSettings = ({ tenantId = 'mylittlepony', farmName = 'el chapo', feature = 'dashboard', table = 'settings', fallback = DEFAULT_PAGE_SETTINGS, farm = null }) => {
    const db = useContext(DBContext) // Directly use DBContext
    const paramsRef = useRef({ tenantId, farmName, feature, table, fallback, farm })
    const [dbReady, setDbReady] = useState(!!window.__dexieDbInstance)

    useEffect(() => {
        paramsRef.current = { tenantId, farmName, feature, fallback, table }
    }, [tenantId, farmName, feature, fallback, table])

    useEffect(() => {
        const checkDbReady = async () => {
            if (window.__dexieDbInstance && !dbReady) {
                setDbReady(true)
            } else if (!window.__dexieDbInstance && dbReady) {
                setDbReady(false)
            }
        }
        checkDbReady()
    }, [dbReady])

    const readSettings = useCallback(async () => {
        if (dbReady && paramsRef.current) {
            const { tenantId, farmName, feature, table, fallback } = paramsRef.current
            let timeoutHandle
            const timeoutPromise = new Promise(resolve => {
                timeoutHandle = setTimeout(() => {
                    const fallbackOptions = fallback[feature] || DEFAULT_PAGE_SETTINGS[feature] || fallback
                    console.info('Operation timed out, using fallback settings', fallbackOptions)
                    resolve({
                        tenant: tenantId,
                        farm: farmName,
                        feature,
                        options: fallbackOptions,
                        timestamp: Date.now(),
                        timeoutReached: true,
                    })
                }, TIMEOUT_DURATION)
            })

            const operationPromise = retryOperation(async () => {
                if (!db[table]) {
                    throw new Error(`Table "${table}" not found in the database`)
                }

                const hasSS = getSessionStorage(`${tenantId}.${farm?.id || farmName}.${feature}.${table}`)
                const fallbackOptions = fallback[feature] || DEFAULT_PAGE_SETTINGS[feature] || fallback

                if (hasSS) {
                    const compareSS = compareOptions(hasSS?.panels ?? [], fallbackOptions?.panels) //session storage is the same. so return it and cancel timer
                    if (compareSS.isSame) {
                        clearTimeout(timeoutHandle)
                        return {
                            tenant: tenantId,
                            farm: farmName,
                            feature,
                            options: hasSS,
                            timestamp: Date.now(),
                        }
                    }
                }

                const results = await db[table]
                    .where({ tenant: tenantId, farm: farmName, feature })
                    .reverse() // Reverse to get the latest record first
                    .sortBy('timestamp')

                const result = results[0]

                const compare = compareOptions(result?.options?.panels ?? [], fallbackOptions?.panels)
                setSessionStorage(`${tenantId}.${farm?.id || farmName}.${feature}.${table}`, fallbackOptions, false)

                if (result?.options !== undefined && compare.isSame) {
                    clearTimeout(timeoutHandle)
                    return result
                } else {
                    if (result?.id && result?.farm && (result?.options === undefined || !compare.isSame)) {
                        await db[table].delete(result.id)
                        console.info(`Deleted incomplete/old record for feature: ${feature}, ID: ${result.id}`)
                    }
                    await db[table].add({
                        tenant: tenantId,
                        farm: farmName,
                        feature,
                        options: fallbackOptions,
                        timestamp: Date.now(),
                    })
                    clearTimeout(timeoutHandle)
                    return {
                        tenant: tenantId,
                        farm: farmName,
                        feature,
                        options: fallbackOptions,
                        timestamp: Date.now(),
                    }
                }
            })
            return Promise.race([operationPromise, timeoutPromise])
        }
    }, [db, dbReady, farm?.id])

    const updateSettings = useCallback(
        async newSettings => {
            console.info('update settings', dbReady, newSettings, paramsRef.current)
            if (dbReady && newSettings && paramsRef.current) {
                const { tenantId, farmName, feature, table } = paramsRef.current
                try {
                    console.info('update settings', newSettings, `${tenantId}.${farm?.id || farmName}.${feature}.${table}`)
                    setSessionStorage(`${tenantId}.${farm?.id || farmName}.${feature}.${table}`, newSettings, false)
                    const existingEntry = await db[table].where({ tenant: tenantId, farm: farmName, feature }).first()
                    if (existingEntry) {
                        // Perform the update
                        const updatedCount = await db[table].update(existingEntry.id, {
                            ...existingEntry,
                            options: newSettings,
                            timestamp: Date.now(),
                        })
                        if (updatedCount === 1) {
                            return true
                        } else {
                            console.error('Update failed')
                            return false
                        }
                    } else {
                        // If the entry doesn't exist, add it
                        const id = await db[table].add({
                            tenant: tenantId,
                            farm: farmName,
                            feature,
                            options: newSettings,
                            timestamp: Date.now(),
                        })
                        console.info('New record added with ID:', id, newSettings)
                        return true
                    }
                } catch (error) {
                    console.error('Error updating settings:', error)
                    throw error
                }
            }
        },
        [db, dbReady, farm?.id], // Updated dependency to paramsRef
    )

    return { readSettings, updateSettings, dbReady }
}

export const useMobAnimals = (selectedMob, getMob, filterAnimals) => {
    const db = useContext(DBContext) // Directly use DBContext
    const [mobAnimals, setMobAnimals] = useState(null) // Store raw data
    const [loading, setLoading] = useState(false)
    const gettingMobAnimals = useRef(false)

    // Fetch mob data from cache or API
    const fetchMobData = useCallback(async () => {
        setLoading(true)
        gettingMobAnimals.current = true

        try {
            const oneHourAgo = Date.now() - 60 * 60 * 1000
            const cachedMob = await db.mobQueries
                .where('id')
                .equals(selectedMob)
                .and(mob => mob.timestamp > oneHourAgo)
                .first()

            if (cachedMob) {
                // Use cached data
                setMobAnimals(cachedMob?.queryResult)
            } else {
                // Fetch from API if no valid cache is found
                const response = await getMob({ id: selectedMob })
                if (response?.data) {
                    const data = filterAnimals ? filterAnimals(response.data) : response.data

                    setMobAnimals(data) // Store raw data
                    // Store the result in Dexie for future caching
                    await db.mobQueries.put({
                        id: selectedMob,
                        queryResult: data,
                        timestamp: Date.now(),
                    })
                }
            }
        } catch (error) {
            console.error('Error fetching mob data:', error)
            setMobAnimals(null)
        } finally {
            setLoading(false)
            gettingMobAnimals.current = false
        }
    }, [db.mobQueries, selectedMob, getMob, filterAnimals])

    // Update mob data (when other components modify it)
    const updateMobData = useCallback(
        async updatedData => {
            try {
                await db.mobQueries.put({
                    id: selectedMob,
                    queryResult: updatedData,
                    timestamp: Date.now(),
                })
                setMobAnimals(updatedData)
            } catch (error) {
                console.error('Error updating mob data:', error)
            }
        },
        [db.mobQueries, selectedMob],
    )

    // Cleanup cached data older than 1 hour
    const cleanUpCache = useCallback(async () => {
        const oneHourAgo = Date.now() - 60 * 60 * 1000
        await db.mobQueries.where('timestamp').below(oneHourAgo).delete()
    }, [db.mobQueries])

    // Effect to trigger data fetching when selectedMob changes
    useEffect(() => {
        if (selectedMob && selectedMob !== 'newMob') {
            fetchMobData()
        } else {
            setMobAnimals(null)
        }
    }, [selectedMob, fetchMobData])

    // Optionally clean up cache when component mounts
    useEffect(() => {
        cleanUpCache().catch(err => console.error('Cache cleanup failed:', err))
    }, [cleanUpCache])

    return { mobAnimals, loading, updateMobData }
}

const retryOperation = async (operation, retries = 5) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
        try {
            return await operation()
        } catch (error) {
            if (error.name === 'DatabaseClosedError' && attempt < retries) {
                console.info(`Attempt ${attempt} failed, retrying...`)
                await new Promise(resolve => setTimeout(resolve, 1000 * attempt)) // Exponential back-off
            } else {
                throw error // Rethrow error if retries exhausted or error is not due to the database being closed
            }
        }
    }
}

function compareOptions(saveSettings, updatedSettings) {
    // Check if the length of updatedSettings is the same as saveSettings to quickly identify differences
    let isSame = saveSettings?.length === updatedSettings?.length

    // Map to store 'group+label+order' as key and 'show' as value for quick lookup
    const saveSettingsMap = new Map(saveSettings.map(item => [`${item.group}+${item.label}+${item.order}`, item.show]))

    const newSettings = updatedSettings.map(item => {
        const key = `${item.group}+${item.label}+${item.order}`
        if (saveSettingsMap.has(key)) {
            // If the item exists, check if 'show' is different and update isSame accordingly
            return { ...item, show: saveSettingsMap.get(key) }
        } else {
            // If the item doesn't exist in saveSettings, it means arrays are different
            isSame = false
            return item // 'show' remains as in updatedSettings since it doesn't exist in saveSettings
        }
    })

    // If arrays are not the same, return the newly formed updatedSettings array
    if (!isSame) {
        return {
            isSame: false,
            newSettings: newSettings,
        }
    }
    // If arrays are the same, return true
    return { isSame: true }
}

export function useUserSettings({ tenantId, farmName, feature, table, fallback, farm }) {
    const [userSettings, setUserSettings] = useState(fallback)
    const { readSettings, updateSettings, dbReady } = useSettings({ tenantId, farmName, feature, table, fallback, farm })
    const prevUserSettingsRef = useRef()
    const isDataFetched = useRef(false)

    // Effect to read settings
    useEffect(() => {
        if (!isDataFetched.current && dbReady) {
            isDataFetched.current = true
            const fetchSettings = async () => {
                const settings = await readSettings()

                if (settings) {
                    setUserSettings(settings?.options)
                    console.info('read settings', settings?.options)
                    prevUserSettingsRef.current = settings?.options
                }
            }
            fetchSettings()
        }
    }, [dbReady, readSettings])

    const resetUserSettings = useCallback(() => {
        if (Object.keys(fallback || {}).length > 0) {
            setUserSettings(fallback)
        } else {
            setUserSettings(DEFAULT_PAGE_SETTINGS?.[feature])
        }
    }, [feature, fallback])

    // Function to update userSettings based on response
    const updateUserSettings = useCallback(
        response => {
            const { event, target, params, noUpdate = false } = response || {}
            console.log('updateUserSettings', event, noUpdate, this)
            if (event !== 'settings') {
                return
            }
            // map only update to dexie on close
            if (noUpdate) {
                const updateDexie = async () => {
                    const newSettings = { ...prevUserSettingsRef.current, map: { ...params } }
                    console.info('updating dexie locally', dbReady, newSettings?.map?.mapTypeId)
                    // save to dexie, but not local state
                    await updateSettings(newSettings, true)
                }
                updateDexie()
                return
            }
            // check for changes
            const base = prevUserSettingsRef.current || userSettings || {}
            const { map, panels } = base
            const newPanels =
                target === 'panels'
                    ? panels?.map(panel => {
                          if (Array.isArray(params?.updates)) {
                              const update = params.updates.find(item => item.order === panel.order)
                              return update ? { ...panel, show: update.show } : panel
                          }
                          return panel.order === params.order ? { ...panel, show: params.show } : panel
                      })
                    : panels
            const newSettings = {
                ...base,
                map,
                panels: newPanels,
            }
            // update dexie and local state
            if (!fastCompare({ map, panels }, newSettings)) {
                // console.info('updating settings', newSettings)
                setUserSettings(newSettings)
            }
        },
        [dbReady, updateSettings, userSettings],
    )

    // Effect to update settings in dexie
    useEffect(() => {
        const saveSettings = async () => {
            if (dbReady && !fastCompare(userSettings, prevUserSettingsRef.current)) {
                console.info('saving settings', userSettings)
                await updateSettings(userSettings)
                const settings = await readSettings()
                console.log('saveSettings', fastCompare(userSettings, fallback), fastCompare(settings, fallback))
                console.groupEnd()
                prevUserSettingsRef.current = userSettings
            }
        }

        saveSettings()
    }, [dbReady, fallback, readSettings, updateSettings, userSettings])

    const panels = useMemo(() => {
        if (!dbReady) return {}
        if (!userSettings?.panels) return {}
        return userSettings?.panels?.reduce((acc, { order, show }) => ({ ...acc, [order]: show }), {})
    }, [userSettings, dbReady])

    const hasPanels = Object.values(panels || {}).some(panel => panel)
    if (feature.includes('rain')) {
        // console.log('useUserSettings', panels, dbReady)
    }

    return { userSettings, updateUserSettings, dbReady, panels, resetUserSettings, hasPanels }
}

// TODO: LOOK TO DO THIS LATER ON AND SIMPLELY USE THE API TO GET THE MOCKS
// ALSO, CREATE A MASTER FAKER FUNCTION THAT CAN BE USED TO CREATE THE MOCKS FOR ALL THE FEATURES
// AND THEN USE THE API TO GET THE MOCKS FOR THE FEATURES
/**
 * Hook to generate, save and load mock asset groups by feature.
 * - createMocks(farm, tenant): clears table then inserts mocks based on location
 * - loadMock(feature): retrieves items for a feature
 */
// export const useMockAssets = () => {
//     const db = useContext(DBContext)
//     const lastCallRef = useRef(0)
//     const lastTenantRef = useRef(null)
//     const writeInFlightRef = useRef(false)
//     const loadCacheRef = useRef({})

//     const loadMock = useCallback(
//         async feature => {
//             if (!db) return []
//             if (loadCacheRef.current[feature]) {
//                 return loadCacheRef.current[feature]
//             }
//             const data = await db.mockAssets.where('feature').equals(feature).toArray()
//             loadCacheRef.current[feature] = data
//             return data
//         },
//         [db],
//     )

//     const createMocks = useCallback(
//         async (farm, tenant) => {
//             if (!db) throw new Error('DB not ready')
//             const now = Date.now()
//             if (now - lastCallRef.current < 500) return
//             lastCallRef.current = now
//             if (tenant === lastTenantRef.current) return
//             lastTenantRef.current = tenant
//             if (writeInFlightRef.current) return
//             writeInFlightRef.current = true

//             const mocks = [
//                 {
//                     feature: 'paddocks',
//                     items: createPaddockAssets({ farm, tenant, useDb: true }),
//                 },
//             ]

//             try {
//                 await db.transaction('rw', db.mockAssets, async () => {
//                     await db.mockAssets.clear()
//                     for (const { feature, items } of mocks) {
//                         const ts = Date.now()
//                         const records = items.map(i => ({ feature, timestamp: ts, ...i }))
//                         await db.mockAssets.bulkPut(records)
//                         loadCacheRef.current[feature] = null
//                     }
//                 })
//             } finally {
//                 writeInFlightRef.current = false
//             }
//         },
//         [db],
//     )

//     return { createMocks, loadMock }
// }

// Usage:
// import { useMockAssets } from 'redux/local-db/dexie-db.js'
// const { createMocks, loadMock } = useMockAssets()
// const initMocks = async () => {
//     await createMocks(farm, tenant)
//     const pad = await loadMock('paddocks')
//     console.log('pad', pad)
// }
// initMocks()
